/* eslint-disable react/prop-types */
/**
 * Phase 8.6 — RogueModulesCard
 *
 * Owner-only card for /settings/platform that lets the tenant owner
 * toggle the 9 canonical modules on/off. Enforces the minimum-bundle
 * rules client-side (mirror of the backend `validate_bundle()`) and
 * surfaces the upsell pricing on each card.
 *
 * Trust Contract: NO modals — bundle violations render as inline
 * red banners; per-module toggles are inline switches.
 *
 * Resolved via:
 *   GET  /api/_modules/manifest    — public manifest (canonical 9 + metadata)
 *   GET  /api/tenants/me/modules   — current enabled set
 *   PUT  /api/tenants/{tid}/modules — owner-only save (back-validated)
 */
import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Sparkles, Lock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import {
  CANONICAL_MODULES,
  MODULE_META,
  validateBundleClient,
} from "../lib/manifest";

export default function RogueModulesCard() {
  const { user } = useAuth();
  const { tenantId, modules: ctxModules, refresh } = useBranding();
  const [manifest, setManifest] = useState(null);
  const [local, setLocal] = useState({}); // editable local state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedTick, setSavedTick] = useState(false);

  // Load full manifest once
  useEffect(() => {
    api.get("/_modules/manifest")
      .then((r) => setManifest(r.data))
      .catch(() => setManifest({ manifest: {} }));
  }, []);

  // Hydrate local state from context whenever it changes
  useEffect(() => {
    const next = {};
    for (const k of CANONICAL_MODULES) {
      next[k] = ctxModules?.[k] !== false; // default ON for anchor tenant
    }
    setLocal(next);
  }, [ctxModules]);

  const validation = useMemo(() => validateBundleClient(local), [local]);
  const isOwner = user?.role === "owner";
  const isAnchor = tenantId === "hidroverse";

  const onToggle = (key) => {
    setLocal((prev) => ({ ...prev, [key]: !prev[key] }));
    setError("");
    setSavedTick(false);
  };

  const onSave = async () => {
    if (!isOwner) return;
    if (!validation.ok && !isAnchor) {
      setError(validation.error);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.put(`/tenants/${tenantId}/modules`, { modules: local });
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 2500);
      // Re-hydrate branding so the sidebar updates immediately
      await refresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Save failed — check the bundle rules.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!manifest) {
    return (
      <div className="hv-card p-5" data-testid="rogue-modules-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#a0aabf]" />
      </div>
    );
  }

  return (
    <div className="hv-card p-5" data-testid="rogue-modules-card" id="modules">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-violet-500/15 border border-violet-400/40 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">Rogue Modules</h2>
            <p className="text-sm text-[#a0aabf] max-w-lg mt-1">
              Pick the operations you want Rogue to power. Each module is
              standalone; bundles unlock cross-module workflows.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {savedTick ? (
            <span className="text-xs text-emerald-300 inline-flex items-center gap-1" data-testid="modules-saved-tick">
              <Check className="w-3 h-3" /> Saved
            </span>
          ) : null}
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !isOwner}
            data-testid="modules-save-btn"
            className="hv-btn hv-btn-primary disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Save modules
          </button>
        </div>
      </div>

      {!isOwner ? (
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 mb-3 inline-flex items-center gap-2"
          data-testid="modules-readonly-banner"
        >
          <Lock className="w-3 h-3" /> Read-only — only the tenant owner can change modules.
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200 mb-3"
          data-testid="modules-bundle-error"
        >
          {error}
        </div>
      ) : null}

      <div className="grid sm:grid-cols-2 gap-3" data-testid="modules-grid">
        {CANONICAL_MODULES.map((key) => {
          const m = manifest.manifest?.[key];
          const meta = MODULE_META[key];
          if (!m || !meta) return null;
          const enabled = !!local[key];
          const priceLabel = meta.isAddon
            ? `$${meta.price}/mo flat`
            : `$${meta.price}/user/mo`;
          return (
            <div
              key={key}
              className={
                "rounded-xl border p-3.5 transition-colors " +
                (enabled
                  ? "bg-white/[0.03]"
                  : "bg-white/[0.01] opacity-80 hover:opacity-100")
              }
              style={{
                borderColor: enabled ? `${meta.color}66` : "rgba(255,255,255,0.08)",
              }}
              data-testid={`module-card-${key}`}
              data-module-enabled={enabled ? "true" : "false"}
            >
              <div className="flex items-start gap-3">
                <span
                  className="size-2 rounded-full mt-1.5 shrink-0"
                  style={{ background: meta.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3
                      className="font-semibold text-sm"
                      style={{ color: enabled ? meta.color : "#FFFFFFCC" }}
                    >
                      {m.name}
                    </h3>
                    {meta.isAddon ? (
                      <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">
                        add-on
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-[#a0aabf] mt-0.5">{m.verb}</p>
                  <p className="text-[11px] text-[#647087] mt-1.5 italic">
                    "{m.headline}"
                  </p>
                  <p className="text-[11px] text-white/70 mt-2 font-medium">
                    {priceLabel}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  disabled={!isOwner}
                  onClick={() => onToggle(key)}
                  data-testid={`module-toggle-${key}`}
                  className={
                    "relative w-10 h-5 rounded-full border transition shrink-0 " +
                    (enabled
                      ? "border-transparent"
                      : "bg-white/5 border-white/15 hover:border-white/25") +
                    (!isOwner ? " cursor-not-allowed opacity-50" : "")
                  }
                  style={{
                    background: enabled ? meta.color : undefined,
                  }}
                >
                  <span
                    className={
                      "absolute top-0.5 size-4 rounded-full bg-white transition-all " +
                      (enabled ? "left-[1.4rem]" : "left-0.5")
                    }
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-[#647087] mt-4">
        Bookkeep, Connect and Cerebrum AI require at least one foundation module.
        Disable rules are enforced server-side at save.
      </p>
    </div>
  );
}
